import React from 'react';

import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';
import { useAppContext } from 'src/components/app-context';
import { usePriceRowContext } from '../../../price-row-context';

import { Direction } from '@prisjakt/ui';
import { BadgeSize } from '@prisjakt/ui/badge';
import { IconLeaf } from '@prisjakt/ui/icon/leaf';
import { FontEmphasis, FontWeight } from '@prisjakt/ui/text/variants';
import { Tooltip } from '@prisjakt/ui/tooltip';
import { OfferPriceLabel } from 'src/components/offer/price/offer-price';
import { PriceLabel } from 'src/components/offer/price/price-label';
import { AuthorizedDealerBadge } from '../authorized-dealer';
import { DeliveryTime } from '../delivery-time';
import { CountryBadge } from './country-badge';
import { PriceTypeNoticeBadge } from './price-type-badge';
import { StockBadge } from './stock-badge';

import { getCampaignByOfferId } from '../../../utils';

import {
  EligibilityEnum,
  FilterType,
  SustainabilityEnum,
} from 'src/components/price-list/types';
import type { ProductOffer, Shipping, Shippings } from 'src/types';

import { OptionTypeEnum } from 'src/components/filter-dropdown';
import {
  CenteredFlexBox,
  Column,
  FirstRow,
  Row,
  StyledCaptionText,
  StyledSustainabilityBadge,
} from './styles';
import { ViewSize } from '../../types';

interface IStockPriceInfo {
  alternativeOffer?: ProductOffer;
}

export const StockPriceInfo: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<IStockPriceInfo>>
> = props => {
  const { alternativeOffer } = props;
  const { t } = useAppContext();
  const { campaign, isFeatured, offer, sorting, viewSize } =
    usePriceRowContext();
  const { getActiveFilters, getPriceDisplay } = useOfferListFilterContext();

  const { isInternationalPrice, store } = offer;

  const getShippingBySort = (shipping?: Shippings | null) => {
    return sorting === OptionTypeEnum.FASTEST_DELIVERY
      ? shipping?.fastest
      : shipping?.cheapest;
  };

  const getCheapestShippingNonMember = (shipping?: Shipping[]) => {
    if (!shipping) {
      return null;
    }

    const cheapestShippingNonMember = shipping
      .filter(
        eligibilityCheck =>
          eligibilityCheck.eligibility !== EligibilityEnum.MEMBERS
      )
      .reduce((prev: Shipping | null, curr) => {
        if (prev === null) return curr;
        const prevCost = prev.shippingCost || 0;
        const currCost = curr.shippingCost || 0;
        return prevCost < currCost ? prev : curr;
      }, null);

    return cheapestShippingNonMember?.shippingCost || null;
  };

  // Get props from alternative offer if supplied
  const id = alternativeOffer ? alternativeOffer.id : offer.priceId;
  const selectedOffer = alternativeOffer || offer;
  const price = alternativeOffer ? alternativeOffer.price : offer.price;
  const offerPrices = alternativeOffer
    ? alternativeOffer.offerPrices
    : offer.offerPrices;
  const shipping = alternativeOffer
    ? getShippingBySort(alternativeOffer.shipping)
    : getShippingBySort(offer.shipping);
  const stock = alternativeOffer ? alternativeOffer.stock : offer.stock;
  const priceAvailability = alternativeOffer
    ? alternativeOffer.availability
    : offer.availability;

  const activeCampaign = getCampaignByOfferId(id, campaign);

  const isDownload = offer.availability?.condition === 'Download';
  const hasSustainableShipping =
    shipping?.sustainability === SustainabilityEnum.SUSTAINABLE;

  const hasMemberShipping = shipping?.eligibility === EligibilityEnum.MEMBERS;

  const getDeliveryText = () => {
    if (hasMemberShipping) {
      const cheapestShippingNonMember: number | null =
        getCheapestShippingNonMember(
          offer.shipping?.nodes || alternativeOffer?.shipping?.nodes
        );

      if (cheapestShippingNonMember !== shipping?.shippingCost) {
        return (
          <span>
            {shipping?.carrier ? `${shipping?.carrier}, ` : null}
            (
            <PriceLabel
              value={cheapestShippingNonMember}
              weight={FontWeight.NORMAL}
            />
            ) {t('product', 'price.free-shipping')}
          </span>
        );
      }
    }

    if (shipping?.shippingCost === 0) {
      return (
        <span>
          {shipping?.carrier ? `${shipping?.carrier}, ` : null}
          {t('product', 'price.free-shipping')}
        </span>
      );
    }

    if (!shipping?.shippingCost) {
      return (
        <span>
          {shipping?.carrier ? `${shipping?.carrier}, ` : null}
          {t('product', 'price.unknown-shipping')}
        </span>
      );
    }

    // Prefix the shipping cost with carrier or shipping label
    return shipping?.carrier ? (
      <>
        {shipping?.carrier},{' '}
        <PriceLabel value={shipping.shippingCost} weight={FontWeight.NORMAL} />
      </>
    ) : (
      <>
        {t('product', 'delivery-time.label')}{' '}
        <PriceLabel value={shipping.shippingCost} weight={FontWeight.NORMAL} />
      </>
    );
  };

  const deliveryText = getDeliveryText();

  const showDeliveryText = () => {
    if (!deliveryText) return false;

    const filteredOnShippingInfo = Boolean(
      getActiveFilters().find(
        item =>
          item.type === FilterType.PRICES_INC_SHIPPING ||
          item.type === FilterType.DELIVERY_METHOD ||
          item.type === FilterType.CARRIERS
      )
    );

    return (
      sorting === OptionTypeEnum.FASTEST_DELIVERY || filteredOnShippingInfo
    );
  };

  return (
    <Column>
      <FirstRow>
        {!isFeatured && viewSize >= ViewSize.MEDIUM && (
          <CenteredFlexBox>
            {isInternationalPrice && store?.primaryMarket && (
              <CountryBadge countryCode={store.primaryMarket} />
            )}
          </CenteredFlexBox>
        )}
        {price && (
          <>
            {!isFeatured && (
              <StockBadge featuredOffer={isFeatured} status={stock?.status} />
            )}
            <OfferPriceLabel
              campaignColors={activeCampaign?.media?.colors}
              container="h4"
              priceDisplay={getPriceDisplay()}
              offer={selectedOffer}
              shipping={shipping ?? undefined}
              offerPrice={offerPrices}
            />
          </>
        )}
      </FirstRow>
      {isFeatured && (
        <>
          {showDeliveryText() ? (
            <Row>
              {hasSustainableShipping && (
                <Tooltip
                  placement={Direction.Up}
                  title={t('product', 'shipping.sustainable')}
                >
                  <StyledSustainabilityBadge
                    circular
                    color="GREEN"
                    icon={IconLeaf}
                    size={BadgeSize.SMALL}
                    $addExtraSpacing={Boolean(offer.authorizedDealer)} // Added to create extra spacing between AD and Sustainable badges in UI
                  />
                </Tooltip>
              )}
              <StyledCaptionText emphasis={FontEmphasis.MEDIUM}>
                {deliveryText}
              </StyledCaptionText>
            </Row>
          ) : null}

          <Row>
            {!alternativeOffer &&
              offer.authorizedDealer &&
              viewSize !== ViewSize.MEDIUM && <AuthorizedDealerBadge />}

            {viewSize >= ViewSize.MEDIUM && (
              <>
                {priceAvailability && (
                  <PriceTypeNoticeBadge priceAvailability={priceAvailability} />
                )}
                {isInternationalPrice && store?.primaryMarket && (
                  <CountryBadge countryCode={store.primaryMarket} />
                )}
                {!isDownload && (
                  <StockBadge
                    featuredOffer={isFeatured}
                    status={stock?.status}
                  />
                )}
              </>
            )}

            {!isDownload && viewSize === ViewSize.SMALL && (
              <StockBadge featuredOffer={isFeatured} status={stock?.status} />
            )}

            {!isDownload && (
              <DeliveryTime
                shipping={shipping}
                stockStatus={stock?.status}
                availability={priceAvailability}
              />
            )}
          </Row>

          {!alternativeOffer &&
            offer.authorizedDealer &&
            viewSize === ViewSize.MEDIUM && (
              <Row>
                <AuthorizedDealerBadge />
              </Row>
            )}
        </>
      )}
    </Column>
  );
};
